import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Accentfärg per system: gold (TGA/Tiego) eller teal (TLE). */
export type Accent = "gold" | "teal";
/** Bakgrund: ljus (default), dämpad grå eller mörk navy (gradient-hero). */
export type SectionTone = "default" | "muted" | "dark";

const GRID_OVERLAY =
  "absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]";

interface SectionProps {
  tone?: SectionTone;
  className?: string;
  id?: string;
  children: ReactNode;
}

export function Section({ tone = "default", className, id, children }: SectionProps) {
  return (
    <section
      id={id}
      className={cn(
        "relative py-20 lg:py-28",
        tone === "muted" && "bg-muted/40",
        tone === "dark" && "gradient-hero overflow-hidden",
        className,
      )}
    >
      {tone === "dark" && <div className={GRID_OVERLAY} />}
      <div className="container relative mx-auto px-6 lg:px-8">{children}</div>
    </section>
  );
}

export const gridOverlayClass = GRID_OVERLAY;

interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  accent?: Accent;
  dark?: boolean;
  center?: boolean;
  className?: string;
}

/** Eyebrow + H2 + ingress enligt mönstret i TiegoStylingGuide.md. */
export function SectionHeading({ eyebrow, title, lead, accent = "gold", dark, center, className }: SectionHeadingProps) {
  return (
    <div className={cn("max-w-3xl", center && "mx-auto text-center", className)}>
      {eyebrow && (
        <span
          className={cn(
            "mb-4 inline-block text-sm font-bold uppercase tracking-widest",
            accent === "teal" ? "text-teal" : "text-gold",
          )}
        >
          {eyebrow}
        </span>
      )}
      <h2
        className={cn(
          "font-display text-3xl font-normal leading-tight md:text-4xl lg:text-5xl",
          dark ? "text-white" : "text-foreground",
        )}
      >
        {title}
      </h2>
      {lead && (
        <p className={cn("mt-5 text-lg leading-relaxed", dark ? "text-white/65" : "text-muted-foreground")}>{lead}</p>
      )}
    </div>
  );
}

/** Ett framhävt ord i en rubrik (kursivt, i accentfärg). */
export function Em({ children, accent = "gold", dark }: { children: ReactNode; accent?: Accent; dark?: boolean }) {
  return (
    <em className={cn("italic", accent === "teal" ? "text-teal" : dark ? "gradient-text" : "text-gold")}>{children}</em>
  );
}

/** Litet chip, t.ex. för standarder (K3, IFRS 16). */
export function Chip({ children, dark }: { children: ReactNode; dark?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
        dark ? "border-white/15 bg-white/10 text-white/85" : "border-border bg-background text-foreground/80",
      )}
    >
      {children}
    </span>
  );
}

/** Kort med ljus bakgrund – samma uttryck som övriga kort på sajten. */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("rounded-2xl border border-border/50 bg-card p-7 shadow-card lg:p-8", className)}>{children}</div>;
}
